import classNames from 'classnames'
import { AnimatePresence, motion } from 'framer-motion'

import { SecondsSinceTick } from '@apps-common/components/common'
import { VertSpacerSm } from '@apps-common/components/layout'
import { commonIcons } from '@apps-common/config/icons'
import { sortAlphabetically } from '@apps-common/helpers/array-sort'
import { useReceiversFilterStore } from '@apps-common/stores'
import { ICloudReceiverModel } from '@apps-common/types/portal/cloud-receiver'
import { useMemo } from 'react'

enum ReceiverGatewayUsage {
  unknown = 'unknown',
  az1 = 'az1',
  az2 = 'az2'
}

interface IGateway {
  serverId: string
  up: number
  upDown: number
  down: number
}

interface IGatewayServer {
  id: string
  timestamp: string
  frequency: number
}

interface IProps {
  loading: boolean
  data: ICloudReceiverModel | undefined
}

export const ReceiverGatewayView: React.FC<IProps> = ({ loading, data }) => {
  const filter = useReceiversFilterStore()

  let currentGatewayAz = ReceiverGatewayUsage.unknown

  if (data?.currentAzKey?.toLowerCase().includes(ReceiverGatewayUsage.az1)) {
    currentGatewayAz = ReceiverGatewayUsage.az1
  }

  if (data?.currentAzKey?.toLowerCase().includes(ReceiverGatewayUsage.az2)) {
    currentGatewayAz = ReceiverGatewayUsage.az2
  }

  const gatewayServers: IGatewayServer[] = useMemo(() => {
    return data?.gateways?.map((g) => ({ id: g.serverId, timestamp: g.timeStamp, frequency: g.frequency })) || []
  }, [data])

  const azGateways = useMemo(() => {
    const az1Servers = gatewayServers
      .filter((g) => g.id.toLowerCase().includes(ReceiverGatewayUsage.az1))
      .sort((a, b) => sortAlphabetically(a, b, 'id'))
    const az2Servers = gatewayServers
      .filter((g) => g.id.toLowerCase().includes(ReceiverGatewayUsage.az2))
      .sort((a, b) => sortAlphabetically(a, b, 'id'))
    const unknownServers = gatewayServers
      .filter((g) => !g.id.toLowerCase().includes(ReceiverGatewayUsage.az1) && !g.id.toLowerCase().includes(ReceiverGatewayUsage.az2))
      .sort((a, b) => sortAlphabetically(a, b, 'id'))

    return [
      {
        gateway: ReceiverGatewayUsage.az1,
        servers: az1Servers
      },
      {
        gateway: ReceiverGatewayUsage.az2,
        servers: az2Servers
      },
      {
        gateway: ReceiverGatewayUsage.unknown,
        servers: unknownServers
      }
    ]
  }, [gatewayServers])

  const servers: IGateway[] = useMemo(() => {
    return (
      data?.gateways?.map((g) => ({
        serverId: g.serverId,
        up: g.companies.filter((c) => !c.paths.some((p) => !p.online)).length,
        upDown: g.companies.filter((c) => c.paths.some((p) => p.online) && c.paths.some((p) => !p.online)).length,
        down: g.companies.filter((c) => !c.paths.some((p) => p.online)).length
      })) || []
    )
  }, [data])

  return (
    <>
      {loading &&
        Array.from(Array(2).keys()).map((k) => (
          <div
            key={`receiver-gateway-list-skeleton-${k}`}
            className='flex flex-col gap-[10px] px-3 py-4 rounded-3xl scrollbar-none bg-skeleton border border-skeleton-border animate-pulse'
          >
            <div className='flex items-center justify-between w-full'>
              <div className='pl-[10px] bg-skeleton-hi w-[100px] h-[24px] rounded-xl' />
              <div>
                <div className='bg-skeleton-hi rounded-full flex items-center justify-between gap-1 px-[4px] py-[2px]'>
                  <div className='h-[14px] w-[35px] rounded-lg bg-skeleton-hi' />
                  <div className='bg-skeleton-hi w-[18px] h-[18px] rounded-full' />
                </div>
              </div>
            </div>
            <VertSpacerSm />
            <AnimatePresence>
              {Array.from(Array(4).keys()).map((ki) => (
                <motion.div key={`receiver-gateway-list-skeleton-${k}-${ki}`} className='bg-skeleton-hi w-full rounded-3xl'>
                  <div className='flex flex-col gap-3 items-start justify-between px-4 py-3'>
                    <div className='bg-skeleton-hi rounded-3xl h-[18px] min-w-[100px]' />
                    <div className='w-full flex items-center justify-between gap-[2px]'>
                      <div className='flex items-center justify-center gap-[2px]'>
                        <div className='h-[28px] w-[50px] pl-[2px] pr-[5px] overflow-hidden bg-skeleton-hi rounded-full flex items-center justify-between gap-[2px]'>
                          <div className='bg-skeleton-hi min-w-[22px] min-h-[22px] flex items-center justify-center rounded-full' />
                        </div>
                      </div>
                      <div className='h-[25px] min-w-[92px] bg-skeleton-hi rounded-full p-1'>
                        <div className='bg-skeleton-hi h-[20px] w-[20px] rounded-full' />
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        ))}
      {!loading &&
        azGateways.map((g, gi) => {
          if (g.servers.length === 0) return null

          return (
            <div
              key={`receiver-gateway-list-${g.gateway}-${gi}`}
              className={classNames(
                'flex flex-col gap-[10px] px-3 py-4 rounded-3xl scrollbar-none bg-surface border border-border',
                g.gateway === currentGatewayAz && 'ring-1 ring-success',
                g.gateway !== currentGatewayAz && 'ring-1 ring-warning'
              )}
            >
              <div className='flex items-center justify-between w-full'>
                <div className='pl-[10px] text-[0.9em] text-text'>{g.gateway.toUpperCase()}</div>
                <div>
                  {g.gateway === currentGatewayAz && (
                    <div className='text-[0.8rem] bg-success/40 text-on-success border border-success rounded-full flex items-center justify-between gap-1 p-[2px] pl-2'>
                      In-use
                      <div className='bg-success w-[18px] h-[18px] rounded-full border border-border' />
                    </div>
                  )}
                  {g.gateway !== currentGatewayAz && (
                    <div className='text-[0.8rem] bg-warning/40 text-on-warning border border-warning rounded-full flex items-center justify-between gap-1 p-[2px] pl-2'>
                      Standby
                      <div className='bg-warning w-[18px] h-[18px] rounded-full border border-border animate-pulse' />
                    </div>
                  )}
                </div>
              </div>
              <VertSpacerSm />
              <AnimatePresence>
                {g.servers.map((s, i) => {
                  const server = servers.find((srv) => srv.serverId === s.id)

                  if (!server) return null

                  return (
                    <motion.div
                      key={`receiver-gateway-list-${ReceiverGatewayUsage.az1}-${s.id}-${i}`}
                      className='bg-surface-2 w-full rounded-3xl border border-border'
                    >
                      <div className='flex flex-col gap-3 items-start justify-between px-4 py-3'>
                        <div className='text-text border border-border jost-medium text-[0.75em] lg:text-[0.9em] rounded-3xl px-2 py-1 leading-[1em] whitespace-nowrap text-ellipsis overflow-hidden'>
                          {server?.serverId}
                        </div>
                        <div className='w-full flex items-center justify-between gap-[2px] overflow-hidden'>
                          <div className='flex items-center justify-center gap-[2px]'>
                            {server.up > 0 && (
                              <button
                                type='button'
                                className='h-[28px] pl-[2px] pr-[5px] overflow-hidden bg-success/30 text-onsuccess border border-success rounded-3xl flex items-center justify-between gap-[2px]'
                                onClick={(e) => {
                                  e?.stopPropagation()

                                  filter.setServerId(server.serverId)
                                  filter.setTab('online')
                                }}
                              >
                                <span className='bg-success min-w-[22px] min-h-[22px] flex items-center justify-center text-[0.75em] rounded-xl jost-medium'>
                                  {server.up}
                                </span>
                                <commonIcons.receiverOnline className='text-[1rem] text-on-success' />
                              </button>
                            )}
                            {server.upDown > 0 && (
                              <button
                                type='button'
                                className='h-[28px] pl-[2px] pr-[5px] overflow-hidden bg-warning/30 text-on-warning border border-warning rounded-3xl flex items-center justify-between gap-[2px]'
                                onClick={(e) => {
                                  e?.stopPropagation()

                                  filter.setServerId(server.serverId)
                                  filter.setTab('degraded')
                                }}
                              >
                                <span className='bg-warning min-w-[22px] min-h-[22px] flex items-center justify-center text-[0.75em] rounded-xl jost-medium'>
                                  {server.upDown}
                                </span>
                                <commonIcons.receiverDegraded className='text-[1rem] text-on-warning' />
                              </button>
                            )}
                            {server.down > 0 && (
                              <button
                                type='button'
                                className='h-[28px] pl-[2px] pr-[5px] overflow-hidden bg-error/30 text-on-error border border-error rounded-3xl flex items-center justify-between gap-[2px]'
                                onClick={(e) => {
                                  e?.stopPropagation()

                                  filter.setServerId(server.serverId)
                                  filter.setTab('offline')
                                }}
                              >
                                <span className='bg-error min-w-[22px] min-h-[22px] flex items-center justify-center text-[0.75em] rounded-xl jost-medium'>
                                  {server.down}
                                </span>
                                <commonIcons.receiverOffline className='text-[1rem] text-on-error' />
                              </button>
                            )}
                          </div>
                          <SecondsSinceTick
                            id={`receiver-gateway-list-${ReceiverGatewayUsage.az1}-${s.id}-${i}-timer`}
                            timestamp={s.timestamp}
                            frequency={s.frequency}
                            className='h-[25px] px-2 rounded-full border'
                            normalClassName='bg-success/60 text-on-success border-success'
                            warningClassName='bg-warning/60 text-on-warning border-warning'
                            warningAfterSeconds={55}
                            dangerClassName='bg-error/60 text-on-error border-error'
                            dangerAfterSeconds={90}
                          />
                        </div>
                      </div>
                    </motion.div>
                  )
                })}
              </AnimatePresence>
            </div>
          )
        })}
    </>
  )
}
